import React from "react";
import {
  DollarSign,
  FileText,
  TrendingUp,
  Users,
  Target,
  UserPlus,
  Building,
  Loader2,
  Phone,
  PhoneCall,
  DoorOpen,
  Car,
  Map,
  UserX,
  Handshake,
  Footprints,
  ChevronDown,
  Info,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";
import { useAgencyQueryInvalidation } from "@/app/_module/hooks/useAgencyQueryInvalidation";
import { useQueryClient } from "@tanstack/react-query";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";

import { useAgencyStats } from "../../hooks/use-hook";
import {
  useAgencyRouteSettings,
  useAgencyRouteStats,
  useAgencyLiveAgents,
} from "../../hooks/use-agency-routes";
import agencyQueryKeys from "../../services/keys";

// Matches the live-map dot colors in AgencyRoutesTab so the dropdown reads the
// same as Agency Center → Routes.
function statusDotColor(statusLabel: string): string {
  switch (statusLabel) {
    case "Knocking":
      return "bg-emerald-500";
    case "Between stops":
      return "bg-blue-500";
    case "Starting":
      return "bg-amber-500";
    default:
      return "bg-gray-400";
  }
}

interface AgencyWideSummaryProps {
  period?: '6-months' | '12-months' | 'year-to-date';
  startDate?: string;
  endDate?: string;
  // Jumps the dashboard to Agency Center → Routes (the live map lives there).
  // Provided by AgencyStats only when routes are enabled.
  onOpenRoutes?: () => void;
  // Jumps the dashboard to the Agents tab below.
  onOpenAgents?: () => void;
}

export const AgencyWideSummary = function({ period = '6-months', startDate, endDate, onOpenRoutes, onOpenAgents }: AgencyWideSummaryProps) {
  // Use state to track current agency ID and force re-render on agency change
  const [currentAgencyId, setCurrentAgencyId] = React.useState<string | null>(
    AgencyTokenManager.getCurrentAgencyId()
  );
  const queryClient = useQueryClient();

  // Listen for agency changes and update the current agency ID
  React.useEffect(() => {
    const handleAgencyChange = () => {
      const newAgencyId = AgencyTokenManager.getCurrentAgencyId();
      setCurrentAgencyId(newAgencyId);
    };

    window.addEventListener('insuracentral-agency-change', handleAgencyChange);
    return () => {
      window.removeEventListener('insuracentral-agency-change', handleAgencyChange);
    };
  }, []);

  // Invalidate queries when agency changes to prevent data duplication
  useAgencyQueryInvalidation();

  // Additional specific invalidation for agency stats when agency changes
  React.useEffect(() => {
    if (currentAgencyId) {
      queryClient.invalidateQueries({
        queryKey: agencyQueryKeys.stats(),
        refetchType: 'active'
      });
    }
  }, [currentAgencyId, queryClient]);
  
  // Use the hook to fetch real agency stats
  const { data: agencyStatsResponse, isLoading, error } = useAgencyStats(currentAgencyId || '', period, startDate, endDate);

  // Routes feature gating + stats (conditionally rendered)
  const { data: routeSettings } = useAgencyRouteSettings(currentAgencyId || '');
  const routesEnabled = routeSettings?.routesEnabled === true;
  const { data: routeStats, isLoading: isRouteStatsLoading } = useAgencyRouteStats(
    routesEnabled ? (currentAgencyId || '') : '',
    period,
    startDate,
    endDate
  );
  // Live "out right now" feed for the Agents Routing dropdown — same source the
  // Agency Center → Routes live map uses (20s poll).
  const { data: liveAgents } = useAgencyLiveAgents(
    routesEnabled ? (currentAgencyId || '') : ''
  );

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Building className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">
            Agency-Wide Performance Summary
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Loading...
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center h-8">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !agencyStatsResponse) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Building className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">
            Agency-Wide Performance Summary
          </h2>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center min-h-[200px]">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {error ? "Failed to load agency stats" : "No agency selected"}
              </p>
              {!currentAgencyId && (
                <p className="text-xs text-muted-foreground mt-2">
                  Please select an agency from the dropdown in the header
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Use real data from API - the data is directly in agencyStatsResponse, not nested
  const stats = agencyStatsResponse as typeof agencyStatsResponse & {
    soldApprovedPremium?: number;
    underwritingPremium?: number;
    soldApprovedPolicies?: number;
    underwritingPolicies?: number;
    totalCalls?: number;
    totalTalkTimeMinutes?: number;
    outboundCalls?: number;
    inboundCalls?: number;
    smsSent?: number;
    smsReceived?: number;
  };

  const soldPremium = stats.soldApprovedPremium ?? 0;
  const uwPremium = stats.underwritingPremium ?? 0;
  const soldPolicies = stats.soldApprovedPolicies ?? 0;
  const uwPolicies = stats.underwritingPolicies ?? 0;

  const summaryCards = [
    {
      // Gross AND net in ONE tile. They are two readings of the same thing, and
      // splitting them into separate cards pushed the grid to 9 tiles, orphaning
      // the last one on a row of its own. Headline = total reported production;
      // the issued figure sits directly beneath it, so the relationship
      // (what was reported vs what actually issued) reads at a glance.
      title: "Total Production",
      value: `$${Math.round(stats.totalPremium).toLocaleString()}`,
      icon: DollarSign,
      accentBg: "bg-emerald-500/10",
      accentText: "text-emerald-500",
      description: "Agency-wide · reported",
      tooltip:
        "This is your agency total reported production. Everything marked sold and approved as well as in underwriting.",
      subMetric: {
        label: "Total Issued Sold & Approved",
        value: `$${Math.round(soldPremium).toLocaleString()}`,
        tooltip:
          "This is your agency total reported net production. Everything marked with sold and approved strictly shows up here.",
      },
      breakdown: {
        sold: `$${Math.round(soldPremium).toLocaleString()}`,
        uw: `$${Math.round(uwPremium).toLocaleString()}`,
      },
    },
    {
      title: "Total Policies",
      value: stats.totalPolicies.toLocaleString(),
      icon: FileText,
      accentBg: "bg-indigo-500/10",
      accentText: "text-indigo-500",
      description: "Agency-wide · reported",
      tooltip:
        "Every policy marked sold and approved as well as in underwriting.",
      // Mirrors the production tile: reported on top, issued beneath. Keeps the
      // row structurally even instead of one tall tile beside three sparse ones.
      subMetric: {
        label: "Total Issued Sold & Approved",
        value: soldPolicies.toLocaleString(),
        tooltip:
          "Policies strictly marked sold and approved. Anything still in underwriting is excluded.",
      },
      breakdown: {
        sold: soldPolicies.toLocaleString(),
        uw: uwPolicies.toLocaleString(),
      },
    },
    {
      title: "Average Policy Size",
      value: `$${Math.round(stats.averagePolicySize).toLocaleString()}`,
      icon: TrendingUp,
      accentBg: "bg-violet-500/10",
      accentText: "text-violet-500",
      description: "Per policy · reported",
      tooltip:
        "Average annual premium across every policy marked sold and approved as well as in underwriting.",
      subMetric: {
        label: "Average Issued Policy",
        value: `$${soldPolicies > 0 ? Math.round(soldPremium / soldPolicies).toLocaleString() : 0}`,
        tooltip:
          "Average annual premium across issued business only — policies strictly marked sold and approved.",
      },
    },
    {
      title: "Closing Ratio",
      value: `${stats.closingRatio.toFixed(1)}%`,
      subMetric: {
        label: "Issued Closing Ratio",
        // Same denominator as the headline, narrowed to business that has ACTUALLY
        // issued — so the gap between the two is exactly what is still underwriting.
        value: `${(stats.issuedClosingRatio ?? 0).toFixed(1)}%`,
        tooltip:
          "Sold & Approved only, over the same base as the ratio above. The difference between the two is the business still sitting in underwriting.",
      },
      icon: Target,
      accentBg: "bg-amber-500/10",
      accentText: "text-amber-500",
      description: "Agency-wide average",
      tooltip:
        "Every agent in this agency and below. Production — Sold & Approved plus Underwriting, over the same three outcomes (issued, underwriting, lost). Only leads that reached a decision count: bad numbers and DNCs are excluded as disqualified rather than counted as losses, and leads still being worked don't drag it down.",
    },
    {
      title: "Average Call Duration",
      value: `${(stats.averageCallTime || 0).toFixed(1)} min`,
      icon: Phone,
      accentBg: "bg-cyan-500/10",
      accentText: "text-cyan-500",
      description:
        stats.totalTalkTimeMinutes != null
          ? stats.totalTalkTimeMinutes >= 60
            ? `${(stats.totalTalkTimeMinutes / 60).toFixed(1)} hrs total talk time`
            : `${stats.totalTalkTimeMinutes.toLocaleString()} min total talk time`
          : "Per call duration",
      callStats:
        stats.totalCalls != null
          ? {
              total: stats.totalCalls,
              outbound: stats.outboundCalls ?? 0,
              inbound: stats.inboundCalls ?? 0,
              avg: `${(stats.averageCallTime || 0).toFixed(1)} min`,
            }
          : undefined,
    },
    {
      title: "Total Calls",
      value: (stats.totalCalls ?? 0).toLocaleString(),
      icon: PhoneCall,
      accentBg: "bg-teal-500/10",
      accentText: "text-teal-500",
      description:
        stats.totalCalls != null
          ? `${(stats.outboundCalls ?? 0).toLocaleString()} outbound · ${(stats.inboundCalls ?? 0).toLocaleString()} inbound`
          : "Calls this period",
    },
    {
      title: "Total Leads",
      value: stats.newClients.toLocaleString(),
      icon: UserPlus,
      accentBg: "bg-blue-500/10",
      accentText: "text-blue-500",
      description: "Agency-wide this period",
    },
    {
      id: "agent-count",
      title: "Agency Agents",
      value: stats.agentCount.toLocaleString(),
      icon: Users,
      accentBg: "bg-primary/10",
      accentText: "text-primary",
      description: onOpenAgents ? "Tap to view agents" : "Agents, admins and owners",
      tooltip:
        "Everyone in this agency and its sub-agencies — agents, admins and owners, each counted once. The Agency Agents tab and Lead Assignment list the same people without upline owners, so those counts run a little lower.",
    },
  ];

  // Field Routes — broken out of the flat summary grid into its own agency-wide
  // panel below. Ordered as the door-knock funnel (approach → set → close), each
  // metric tinted so an owner can read the story at a glance. `badge` flags the
  // two diagnostic rates as "setting" vs "closing" skill.
  const rLoading = isRouteStatsLoading || !routeStats;
  const routeMetrics = routesEnabled
    ? [
        {
          id: "agents-routing",
          title: "Agents Routing",
          value: rLoading ? "—" : `${routeStats!.agentsRouting} / ${routeStats!.totalAgents}`,
          icon: Map,
          accentBg: "bg-sky-500/10",
          accentText: "text-sky-500",
          badge: null as string | null,
          description: "Knocked ≥1 door this period",
          tooltip:
            "How many of your agents knocked at least one door during the selected date range, out of total members. This is for the SELECTED PERIOD — not who's out routing right now. The live routing map shows real-time activity.",
        },
        {
          id: "doors-knocked",
          title: "Doors Knocked",
          value: rLoading ? "—" : routeStats!.doorsKnocked.toLocaleString(),
          icon: DoorOpen,
          accentBg: "bg-indigo-500/10",
          accentText: "text-indigo-500",
          badge: null as string | null,
          description: rLoading
            ? "Agency-wide this period"
            : `${routeStats!.knockContactRate.toFixed(1)}% answered · ${routeStats!.avgStopsPerRoute.toFixed(1)}/route${routeStats!.doorsPerApp > 0 ? ` · ${routeStats!.doorsPerApp.toFixed(1)} doors/app` : ""}`,
          tooltip:
            "Doors your agents knocked in the selected period. “answered” = the share of knocks where someone was home and answered (a real conversation). “/route” = doors knocked per route. “doors/app” = how many doors it takes to write one application — the single best full-funnel efficiency number (lower is better).",
        },
        {
          id: "refusal-rate",
          title: "Refusal Rate",
          value: rLoading ? "—" : `${routeStats!.refusalRate.toFixed(1)}%`,
          icon: UserX,
          accentBg: "bg-rose-500/10",
          accentText: "text-rose-500",
          badge: "Setting skill",
          description: "Answered but no appointment",
          tooltip:
            "Of the doors where someone ANSWERED, the share who refused to set an appointment / let the agent in. HIGH = the agent is struggling to SET appointments (the door approach needs work), not to close.",
        },
        {
          id: "close-rate",
          title: "Close Rate",
          value: rLoading ? "—" : `${routeStats!.closeRate.toFixed(1)}%`,
          icon: Handshake,
          accentBg: "bg-emerald-500/10",
          accentText: "text-emerald-500",
          badge: "Closing skill",
          description: rLoading
            ? "Of presentations, % sold"
            : `${routeStats!.presentations.toLocaleString()} presentations${routeStats!.presentationsPerSale > 0 ? ` · ${routeStats!.presentationsPerSale.toFixed(1)}/sale` : ""}`,
          tooltip:
            "Of the doors that let the agent PRESENT (no-sale + sold), the share that closed. LOW = the agent gets in but struggles to CLOSE (the selling/presentation needs work). “/sale” = how many presentations it takes to write one app. Together with Refusal Rate this tells you whether an agent's problem is getting in the door or closing once inside.",
        },
        {
          id: "miles-driven",
          title: "Miles Driven",
          value: rLoading ? "—" : `${routeStats!.milesDriven.toFixed(0)} mi`,
          icon: Car,
          accentBg: "bg-amber-500/10",
          accentText: "text-amber-500",
          badge: null as string | null,
          description: "Between knocked doors",
          tooltip:
            "Total distance your agents covered between the doors they knocked in the selected period. Measured from the GPS location of each door — a close estimate of real driving/walking, not exact odometer miles.",
        },
      ]
    : [];

  return (
    <div>
      {/* Section header — matches the Field Routes treatment below */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/20">
            <Building className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold leading-none">
              Performance Overview
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                Agency-wide
              </span>
            </h2>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Sales &amp; activity across your whole agency · selected period
            </p>
          </div>
        </div>
      </div>

      {/* Tinted panel with accent tiles */}
      <div className="rounded-2xl border bg-gradient-to-br from-muted/50 via-background to-background p-3 sm:p-4">
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {summaryCards.map((card) => {
            const bd = (card as { breakdown?: { sold: string; uw: string } }).breakdown;
            const tip = (card as { tooltip?: string }).tooltip;
            const sm = (card as { subMetric?: { label: string; value: string; tooltip: string } }).subMetric;
            const cs = (card as { callStats?: { total: number; outbound: number; inbound: number; avg: string } }).callStats;
            const isAgentCount =
              (card as { id?: string }).id === "agent-count" && !!onOpenAgents;
            return (
              <div
                key={card.title}
                onClick={isAgentCount ? onOpenAgents : undefined}
                role={isAgentCount ? "button" : undefined}
                tabIndex={isAgentCount ? 0 : undefined}
                onKeyDown={
                  isAgentCount
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onOpenAgents?.();
                        }
                      }
                    : undefined
                }
                className={`group relative flex h-full w-full flex-col rounded-xl border bg-card/70 p-3.5 transition-shadow hover:shadow-md ${
                  isAgentCount
                    ? "cursor-pointer hover:border-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    : ""
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    {card.title}
                    {(tip || bd || cs) && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label={bd ? "Breakdown" : cs ? "Call breakdown" : "Info"}
                              className="inline-flex items-center cursor-help"
                            >
                              <Info className="h-3.5 w-3.5 text-primary/70 hover:text-primary transition-colors" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-56 text-xs">
                            {bd ? (
                              <div className="flex flex-col gap-1">
                                {/* Explanation first, then the split. Previously
                                    `bd` short-circuited `tip` entirely, so a card
                                    with both silently lost its wording. */}
                                {!!tip && (
                                  <span className="mb-1 block leading-snug">{tip}</span>
                                )}
                                <span className="font-semibold mb-0.5">Breakdown</span>
                                <span className="flex items-center justify-between gap-3">
                                  <span className="text-emerald-400">Sold &amp; Approved</span>
                                  <span className="font-semibold tabular-nums">{bd.sold}</span>
                                </span>
                                <span className="flex items-center justify-between gap-3">
                                  <span className="text-amber-400">Underwriting</span>
                                  <span className="font-semibold tabular-nums">{bd.uw}</span>
                                </span>
                              </div>
                            ) : cs ? (
                              <div className="flex flex-col gap-1">
                                <span className="font-semibold mb-0.5">Calls this period</span>
                                <span className="flex items-center justify-between gap-3">
                                  <span>Total</span>
                                  <span className="font-semibold tabular-nums">{cs.total.toLocaleString()}</span>
                                </span>
                                <span className="flex items-center justify-between gap-3">
                                  <span className="text-emerald-400">Outbound</span>
                                  <span className="font-semibold tabular-nums">{cs.outbound.toLocaleString()}</span>
                                </span>
                                <span className="flex items-center justify-between gap-3">
                                  <span className="text-blue-400">Inbound</span>
                                  <span className="font-semibold tabular-nums">{cs.inbound.toLocaleString()}</span>
                                </span>
                                <span className="mt-1 flex items-center justify-between gap-3 border-t border-white/15 pt-1 text-muted-foreground">
                                  <span>Avg length</span>
                                  <span className="font-semibold tabular-nums">{cs.avg}</span>
                                </span>
                              </div>
                            ) : (
                              tip
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </span>
                  <div
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${card.accentBg}`}
                  >
                    <card.icon className={`h-4 w-4 ${card.accentText}`} />
                  </div>
                </div>
                {/* Content area flexes to fill the row height and centres
                    itself. The production tile is taller than the rest (it
                    carries a second figure), and the grid stretches every card
                    in the row to match — without centring, the shorter cards
                    read as half-empty with their numbers stranded at the top. */}
                <div className="flex flex-1 flex-col justify-center">
                  <div className="mt-2 text-2xl font-bold tabular-nums tracking-tight">
                    {card.value}
                  </div>
                  <p className="mt-1 text-[11px] leading-tight text-muted-foreground">
                    {card.description}
                  </p>
                  {!!sm && (
                    <div className="mt-2.5 border-t pt-2">
                      <span className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                        {sm.label}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" aria-label="Info" className="inline-flex cursor-help items-center">
                                <Info className="h-3 w-3 text-primary/70 transition-colors hover:text-primary" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-56 text-xs">
                              {sm.tooltip}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </span>
                      <div className="text-lg font-bold tabular-nums tracking-tight text-emerald-600">
                        {sm.value}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {routesEnabled && (
        <div className="mt-8">
          {/* Section header */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 ring-1 ring-primary/20">
                <Footprints className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="flex items-center gap-2 text-base font-semibold leading-none">
                  Field Routes
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                    Agency-wide
                  </span>
                </h3>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Door-knock activity across your whole agency · selected period
                </p>
              </div>
            </div>
          </div>

          {/* Funnel-tinted metric panel */}
          <div className="rounded-2xl border bg-gradient-to-br from-muted/50 via-background to-background p-3 sm:p-4">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
              {routeMetrics.map((m) => {
                const isAgents = m.id === "agents-routing";
                const tile = (
                  <div
                    className={`group relative flex h-full w-full flex-col rounded-xl border bg-card/70 p-3.5 transition-shadow hover:shadow-md ${
                      isAgents
                        ? "cursor-pointer text-left hover:border-sky-500/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40"
                        : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        {m.title}
                        {!isAgents && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  aria-label="Info"
                                  className="inline-flex items-center cursor-help"
                                >
                                  <Info className="h-3.5 w-3.5 text-primary/70 hover:text-primary transition-colors" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-56 text-xs">
                                {m.tooltip}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                      <div
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${m.accentBg}`}
                      >
                        <m.icon className={`h-4 w-4 ${m.accentText}`} />
                      </div>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5 text-2xl font-bold tabular-nums tracking-tight">
                      {m.value}
                      {isAgents && (
                        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-y-0.5" />
                      )}
                    </div>
                    {m.badge && (
                      <span
                        className={`mt-1 inline-flex w-fit rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${m.accentBg} ${m.accentText}`}
                      >
                        {m.badge}
                      </span>
                    )}
                    <p className="mt-1 text-[11px] leading-tight text-muted-foreground">
                      {isAgents ? "Tap to see who's out · " : ""}
                      {m.description}
                    </p>
                  </div>
                );

                if (!isAgents) {
                  return (
                    <div key={m.title} className="h-full">
                      {tile}
                    </div>
                  );
                }

                const live = liveAgents || [];
                const roster = routeStats?.agentBreakdown || [];
                return (
                  <Popover key={m.title}>
                    <PopoverTrigger asChild>
                      <button type="button" className="h-full w-full text-left">
                        {tile}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-72 overflow-hidden p-0">
                      <div className="border-b bg-muted/40 px-3 py-2">
                        <p className="text-xs font-semibold">Who&apos;s routing</p>
                        <p className="text-[11px] text-muted-foreground">
                          Live now + everyone who routed this period
                        </p>
                      </div>

                      {/* Out now — same feed as the Agency Center live map */}
                      <div className="px-3 py-2">
                        <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                          </span>
                          Out now
                        </p>
                        {live.length > 0 ? (
                          <ul className="space-y-1.5">
                            {live.map((a) => (
                              <li
                                key={a.userId}
                                className="flex items-center justify-between gap-2 text-xs"
                              >
                                <span className="flex min-w-0 items-center gap-1.5">
                                  <span
                                    className={`h-2 w-2 shrink-0 rounded-full ${statusDotColor(
                                      a.statusLabel
                                    )}`}
                                  />
                                  <span className="truncate">{a.name}</span>
                                </span>
                                <span className="shrink-0 text-muted-foreground">
                                  {a.statusLabel} · {a.doorsKnockedToday ?? 0}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            No agents out right now.
                          </p>
                        )}
                      </div>

                      {/* Routed this period */}
                      <div className="border-t px-3 py-2">
                        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Routed this period{roster.length ? ` · ${roster.length}` : ""}
                        </p>
                        {roster.length > 0 ? (
                          <ul className="max-h-52 space-y-1 overflow-y-auto">
                            {roster.map((a) => (
                              <li
                                key={a.userId}
                                className="flex items-center justify-between gap-2 text-xs"
                              >
                                <span className="truncate">{a.name}</span>
                                <span className="shrink-0 tabular-nums text-muted-foreground">
                                  {a.doorsKnocked.toLocaleString()} doors · {a.routes.toLocaleString()} {a.routes === 1 ? "route" : "routes"}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            No door knocks in this period yet.
                          </p>
                        )}
                      </div>

                      {onOpenRoutes && (
                        <button
                          type="button"
                          onClick={onOpenRoutes}
                          className="w-full border-t px-3 py-2 text-left text-xs font-medium text-primary hover:bg-muted/40"
                        >
                          Open live map →
                        </button>
                      )}
                    </PopoverContent>
                  </Popover>
                );
              })}
            </div>

            {/* Funnel legend — the door-knock story left to right */}
            <div className="mt-3.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 px-0.5 text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground/70">The funnel:</span>
              <span>Knock</span>
              <span className="text-muted-foreground/50">→</span>
              <span>Answer</span>
              <span className="text-muted-foreground/50">→</span>
              <span className="text-rose-500/90">Set the appt</span>
              <span className="text-muted-foreground/50">→</span>
              <span>Present</span>
              <span className="text-muted-foreground/50">→</span>
              <span className="text-emerald-500/90">Close</span>
              <span className="ml-auto italic">
                High refusal = setting problem · Low close = closing problem
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
